import React, { useState, useEffect } from 'react';
import { ApolloConsumer } from 'react-apollo';
import querystring from 'querystring';

import {
    directLink,
    fTypeLink,
    msDirectLink,
    sfDirectLink,
    attrDirectlink
} from '../utils/directLink';
import {
    morningstarTesters,
    riskPotentialTesters,
    otherAttributesTesters,
    selectFundTesters,
    fTypeTesters
} from '../utils/filter-tests';
import useCategoryState from '../Hooks/useCategoryState';
import useFilterState from '../Hooks/useFilterState';
import useSearchState from '../Hooks/useSearchState';
import { filterFunds } from '../utils/fund-info';

import {
    ETF_FILTERS_QUERY,
    FILTERS_QUERY
} from '../graphql/queries/filters.query';
import mStarCategoriesOrLocal from './useLocalMStarAsFallback';

const FiltersContext = React.createContext();

const filterCategories = (subs, funds) =>
    subs.size
        ? funds.filter((fund) => subs.has(mStarCategoriesOrLocal(fund)))
        : funds;

// @todo instead of filter a whole list...
// pregenerate the groupings, and then get the composite or intersection
// of those groupings
// intersection across filtertypes but composite within
const filter = (filters, funds, testers) =>
    filters.size
        ? funds.filter((fund) => [...filters].some((key) => testers[key](fund)))
        : funds;

const search = (searchText, funds) =>
    searchText
        ? funds.filter((fund) => {
              const stRegex = new RegExp(
                  searchText.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'),
                  'i'
              );
              return stRegex.test(fund.name) || stRegex.test(fund.ticker);
          })
        : funds;

const InternalProvider = ({ children, client, linkedFrom }) => {
    const pathparts = window.location.pathname.split('/')[4];
    const qsMap = querystring.parse(window.location.search.slice(1));
    const rpFilters = [
        Object.keys(riskPotentialTesters).find((key) =>
            riskPotentialTesters[key]({
                riskLevel: parseInt(qsMap.riskLevel, 10)
            })
        )
    ].filter((defined) => defined);

    const catDefaults = directLink(linkedFrom || pathparts);
    const fTypeDefaults = fTypeLink(linkedFrom || pathparts);
    const msDefaults = msDirectLink(linkedFrom || pathparts);
    const rpDefaults = {
        filters: rpFilters,
        pills: rpFilters
    };
    const oaDefaults = attrDirectlink(linkedFrom || pathparts);
    const sfDefaults = sfDirectLink(linkedFrom || pathparts);

    const categoryState = useCategoryState(catDefaults);
    const { selectedSubCategories } = categoryState;
    const ftypeState = useFilterState(fTypeDefaults);
    const morningstarState = useFilterState(msDefaults);
    const riskPotentialState = useFilterState(rpDefaults);
    const otherAttributesState = useFilterState(oaDefaults);
    const selectFundAttributeState = useFilterState(sfDefaults);

    const searchState = useSearchState();
    const [allMutualFunds, setAllMutualFunds] = useState([]);
    const [allSelectFunds, setAllSelectFunds] = useState([]);
    const [allExchangeTradedFunds, setAllETFs] = useState([]);

    useEffect(() => {
        (async () => {
            if (pathparts === 'select-funds') {
                if (allSelectFunds.length) return;

                const {
                    data: { allMutualFunds: funds }
                } = await client.query({ query: FILTERS_QUERY });
                const filteredSelectFunds = funds.filter((f) => f.selectFund);

                setAllSelectFunds(filterFunds(filteredSelectFunds));
                setAllMutualFunds(filterFunds(funds));
            } else if (pathparts === 'etf') {
                if (allExchangeTradedFunds.length) return;

                const {
                    data: { allExchangeTradedFunds: exchangeTradedFunds }
                } = await client.query({ query: ETF_FILTERS_QUERY });

                setAllETFs(exchangeTradedFunds);
            } else {
                if (allMutualFunds.length) return;

                const {
                    data: { allMutualFunds: funds }
                } = await client.query({ query: FILTERS_QUERY });

                setAllMutualFunds(filterFunds(funds));
            }
        })(allMutualFunds, allSelectFunds, allExchangeTradedFunds);
    });

    if (
        !allMutualFunds.length &&
        !allSelectFunds.length &&
        !allExchangeTradedFunds.length
    )
        return false;
    const fundsToBeFiltered =
        {
            etf: allExchangeTradedFunds,
            'select-funds': allSelectFunds
        }[pathparts] || allMutualFunds;

    const fTypeFiltered = filter(
        ftypeState.selectedFilters,
        fundsToBeFiltered,
        fTypeTesters
    );

    const catFiltered = filterCategories(selectedSubCategories, fTypeFiltered);

    const morningstarFiltered = filter(
        morningstarState.selectedFilters,
        catFiltered,
        morningstarTesters
    );

    const riskPotentialFiltered = filter(
        riskPotentialState.selectedFilters,
        morningstarFiltered,
        riskPotentialTesters
    );

    const otherAttributesFiltered = filter(
        otherAttributesState.selectedFilters,
        riskPotentialFiltered,
        otherAttributesTesters
    );

    const selectFundFiltered = filter(
        selectFundAttributeState.selectedFilters,
        otherAttributesFiltered,
        selectFundTesters
    );

    const searchFiltered = search(searchState.searchText, selectFundFiltered);

    const filteredTickers = new Set(searchFiltered.map((f) => f.ticker));

    return (
        <FiltersContext.Provider
            value={{
                categoryState,
                ftypeState,
                morningstarState,
                riskPotentialState,
                otherAttributesState,
                selectFundAttributeState,
                searchState,
                filteredTickers,
                allMutualFunds
            }}
        >
            {children}
        </FiltersContext.Provider>
    );
};

export const FiltersContextProvider = (props) => (
    <ApolloConsumer>
        {(client) => <InternalProvider {...props} client={client} />}
    </ApolloConsumer>
);

export default FiltersContext;
